import React, { useMemo } from 'react';
import { generatePath, Link } from 'react-router-dom';
import { FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText } from '@mui/material';
import BaseMUIDataTable from './BaseMUIDataTable/BaseMUIDataTable';
import { formatCsvFileName, formatDateTime } from '../helpers/formatters';
import { getUserFullName } from '../helpers/user';
import routes from '../routes';
import { sortByDateAndTime } from '../helpers/sorters';
import { rangeFilter } from '../helpers/MuiTableCustomFilters';
import { get } from 'lodash';
import { FF_USER_GROUPS } from './FeatureFlagWrapper';
import useHasActiveFeatureFlag from '../hooks/useFeatureFlags';

const SmsTable = ({ tableId, isLoading, emails = [] }) => {
  const hasUserGroupsFF = useHasActiveFeatureFlag(FF_USER_GROUPS);
  const emailsSentByList = useMemo(
    () =>
      Object.values(
        emails.reduce((acc, email) => {
          acc[email.created_by.id] = email.created_by;
          return acc;
        }, {})
      ),
    [emails.length]
  );

  const createdByCustomBodyRender = (value, csv = false) => {
    const userFullName = getUserFullName(value);

    if (csv) {
      return userFullName;
    }

    return (
      // value set for search to work
      <Link to={generatePath(routes.userEditNew, { id: value.id })} value={userFullName}>
        {userFullName}
      </Link>
    );
  };

  const createdAtCustomBodyRender = value => formatDateTime(value.value, true);

  const renderSentByList = (filterList, onChange, index, column) => (
    <FormControl variant="standard">
      <InputLabel htmlFor="sent_by-selector">{column.label}</InputLabel>
      <Select
        variant="standard"
        id="sent_by-selector"
        multiple
        value={filterList[index]}
        renderValue={selected => selected.join(', ')}
        onChange={event => {
          filterList[index] = event.target.value;
          onChange(filterList[index], index, column);
        }}
      >
        {emailsSentByList.map(user => (
          <MenuItem key={user.id} value={getUserFullName(user)}>
            <Checkbox color="primary" checked={filterList[index].indexOf(getUserFullName(user)) > -1} />
            <ListItemText primary={getUserFullName(user)} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <BaseMUIDataTable
      isLoading={isLoading}
      tableId={tableId}
      data={emails}
      columns={[
        {
          name: 'id',
          label: 'ID',
          options: {
            filter: false,
            sort: false,
            display: false,
          },
        },
        {
          name: 'body',
          label: 'Body',
          options: {
            filter: false,
          },
        },
        {
          name: 'number_of_recipients',
          label: '# of Receivers',
          options: {
            customFilterListOptions: {
              render: value => `# of Receivers: ${value}`,
            },
            filterOptions: {
              names: rangeFilter.names,
              logic: (claimsNo, filters) => rangeFilter.logic(claimsNo, get(filters, [0], '')),
            },
          },
        },
        ...(hasUserGroupsFF
          ? [
              {
                name: 'group',
                label: 'Group',
                options: {
                  display: true,
                  filter: false,
                  customBodyRender: value => (value ? value.name : '-'),
                  customBodyRenderCSV: value => (value ? value.name : '-'),
                },
              },
            ]
          : []),
        {
          name: 'created_by',
          label: 'Sent By',
          options: {
            filterType: 'custom',
            customFilterListOptions: {
              render: value => `Sent By: ${value}`,
            },
            filterOptions: {
              logic: (user, filters) => {
                if (!filters || filters.length === 0) {
                  return false;
                }

                // the link rendered in this column sets "value", so the filter receives the full name as a string
                if (typeof user === 'string') {
                  return !filters.includes(user);
                }

                if (typeof user === 'object') {
                  return !filters.includes(getUserFullName(user));
                }

                return true;
              },
              display: renderSentByList,
            },
            customBodyRender: value => createdByCustomBodyRender(value),
            customBodyRenderCSV: value => createdByCustomBodyRender(value, true),
          },
        },
        {
          name: 'created_at',
          label: 'Sent At',
          options: {
            sortCompare: order => (row1, row2) => sortByDateAndTime(order, row1.data.value, row2.data.value),
            customFilterListOptions: {
              render: value => `Sent At: ${value}`,
            },
            customBodyRender: value => createdAtCustomBodyRender(value),
            customBodyRenderCSV: value => createdAtCustomBodyRender(value),
          },
        },
      ]}
      options={{
        sortOrder: {
          name: 'id',
          direction: 'desc',
        },
        rowsPerPage: 15,
        selectableRows: 'none',
        responsive: 'simple',
        downloadOptions: {
          filename: formatCsvFileName('Sent SMS'),
        },
      }}
    />
  );
};

export default SmsTable;
